export type StaffUserCreationDeliveryOutcome =
  | "SENT"
  | "FAILED"
  | "UNKNOWN"
  | "SUPPRESSED";

export type StaffUserCreationFeedbackSeverity =
  | "success"
  | "warning"
  | "info";

export type StaffUserCreationFeedback = {
  deliveryOutcome: StaffUserCreationDeliveryOutcome;
  inlineSeverity: StaffUserCreationFeedbackSeverity;
  inlineMessage: string;
};

const normalizeDeliveryOutcome = (
  deliveryStatus: string
): StaffUserCreationDeliveryOutcome => {
  if (deliveryStatus === "SENT") {
    return "SENT";
  }

  if (deliveryStatus === "FAILED") {
    return "FAILED";
  }

  if (deliveryStatus === "SUPPRESSED") {
    return "SUPPRESSED";
  }

  // NOT_SENT and unfamiliar future values are deliberately not treated as a
  // successful delivery. The account is committed, but delivery is unknown.
  return "UNKNOWN";
};

export const getStaffUserCreationFeedback = ({
  deliveryStatus,
  displayName,
}: {
  deliveryStatus: string;
  displayName: string;
}): StaffUserCreationFeedback => {
  const normalizedDisplayName = displayName.trim() || "The employee account";
  const deliveryOutcome = normalizeDeliveryOutcome(deliveryStatus);

  if (deliveryOutcome === "SENT") {
    const message = `${normalizedDisplayName} was created and credentials were sent.`;

    return {
      deliveryOutcome,
      inlineSeverity: "success",
      inlineMessage: message,
    };
  }

  if (deliveryOutcome === "FAILED") {
    return {
      deliveryOutcome,
      inlineSeverity: "warning",
      inlineMessage: `${normalizedDisplayName} was created, but credentials could not be sent. Select Generate Employee Login Credentials in the Actions menu to download them, then share the file with the employee securely.`,
    };
  }

  if (deliveryOutcome === "SUPPRESSED") {
    return {
      deliveryOutcome,
      inlineSeverity: "info",
      inlineMessage: `${normalizedDisplayName} was created. This is a test site, so no sign-in email was sent.`,
    };
  }

  return {
    deliveryOutcome,
    inlineSeverity: "warning",
    inlineMessage: `${normalizedDisplayName} was created, but we cannot confirm that the sign-in email was sent. Ask ${normalizedDisplayName} if they got it. If not, select Re-generate Employee Login Credentials in the Actions menu to download new login details, then share the file with the employee securely.`,
  };
};

export type StaffUserCreationNavigationState = {
  staffUserCreationFeedback: StaffUserCreationFeedback;
};
